import { create } from 'zustand';

/** What the person picked; "system" follows the operating system setting. */
export type ThemeChoice = 'dark' | 'light' | 'system';
export type Theme = 'dark' | 'light';

const KEY = 'nova.theme';
const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: light)') : null;

function stored(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'light' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

function resolve(choice: ThemeChoice): Theme {
  if (choice === 'system') return media?.matches ? 'light' : 'dark';
  return choice;
}

function apply(theme: Theme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'light' ? '#eef2f8' : '#070b14');
}

interface ThemeState {
  choice: ThemeChoice;
  theme: Theme;
  choose: (choice: ThemeChoice) => void;
}

export const useTheme = create<ThemeState>((set) => {
  const choice = stored();
  return {
    choice,
    theme: resolve(choice),
    choose: (next) => {
      try {
        localStorage.setItem(KEY, next);
      } catch {
        /* storage blocked: the choice lasts for this page */
      }
      const theme = resolve(next);
      apply(theme);
      set({ choice: next, theme });
    },
  };
});

// Keep "system" in step when the OS switches between light and dark
media?.addEventListener('change', () => {
  const { choice } = useTheme.getState();
  if (choice !== 'system') return;
  const theme = resolve('system');
  apply(theme);
  useTheme.setState({ theme });
});

apply(useTheme.getState().theme);

export const THEME_LABEL: Record<ThemeChoice, string> = {
  dark: 'Dark',
  light: 'Light',
  system: 'Match system',
};
